import React from 'react'
import moment from 'moment'

import EventIcon from '@mui/icons-material/Event'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import {
  getShiftCardStyle,
  getShiftSource,
  getSubcontractorName,
  isDraftRosterShift,
  isOnCallShift,
} from './globalRosterUtils'

const RosterShiftCard = ({
  shift,
  // The card spans its days in the grid, so these only flag continuation
  // beyond the visible date range
  continuesFromPrior = false,
  continuesToNext = false,
  // How many day columns the card occupies on screen. A multi-day shift
  // squeezed into a single column (e.g. Fri 20:00-Sat 08:00 with only
  // Saturday visible) has no room for day names, so it falls back to times
  // only and carries the full range in a tooltip instead.
  visibleSpan = 1,
  duplicateCount = 1,
  pendingApplications,
  onClick,
}) => {
  const shiftStyle = getShiftCardStyle(shift, pendingApplications)
  const onCall = isOnCallShift(shift)
  const draftRoster = isDraftRosterShift(shift)
  const isMultiDay = !moment(shift.start).isSame(moment(shift.end), 'day')
  const timeFormat = isMultiDay && visibleSpan > 1 ? 'ddd HH:mm' : 'HH:mm'
  const fullRange = `${moment(shift.start).format('ddd D MMM HH:mm')} – ${moment(
    shift.end,
  ).format('ddd D MMM HH:mm')}`

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        bgcolor: shiftStyle.bg,
        color: shiftStyle.fg,
        cursor: 'pointer',
        border: onCall ? '1.5px dashed' : '1px solid',
        borderColor: shiftStyle.border,
        animation: draftRoster
          ? 'draftRosterPulse 1.25s ease-in-out infinite'
          : 'none',
        '@keyframes draftRosterPulse': {
          '0%': {
            boxShadow: '0 0 0 0 rgba(35, 154, 164, 0.62)',
          },
          '70%': {
            boxShadow: '0 0 0 12px rgba(35, 154, 164, 0)',
          },
          '100%': {
            boxShadow: '0 0 0 0 rgba(35, 154, 164, 0)',
          },
        },
        '&:hover': {
          boxShadow: draftRoster ? '0 8px 20px rgba(35,154,164,0.24)' : 2,
          filter: 'brightness(0.98)',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          {shift.event ? (
            <EventIcon fontSize="small" sx={{ color: shiftStyle.fg }} />
          ) : onCall ? (
            <PhoneInTalkIcon fontSize="small" sx={{ color: shiftStyle.fg }} />
          ) : shift.roster ? (
            <ViewWeekIcon fontSize="small" sx={{ color: shiftStyle.fg }} />
          ) : (
            <WorkOutlineIcon fontSize="small" sx={{ color: shiftStyle.fg }} />
          )}
          <Tooltip title={isMultiDay ? fullRange : ''}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="inherit"
              noWrap
            >
              {continuesFromPrior ? '← ' : ''}
              {moment(shift.start).format(timeFormat)}-
              {moment(shift.end).format(timeFormat)}
              {continuesToNext ? ' →' : ''}
            </Typography>
          </Tooltip>
        </Stack>
        {pendingApplications.length > 0 && (
          <Tooltip
            title={`${pendingApplications.length} pending application${
              pendingApplications.length === 1 ? '' : 's'
            }`}
          >
            <HowToRegIcon fontSize="small" sx={{ color: shiftStyle.fg }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" display="block" noWrap color="inherit">
        {shift.clinicalRole || 'Unspecified'}
      </Typography>
      <Stack direction="row" gap={1}>
        {shift.assignedSubcontractor && (
          <Typography variant="caption" display="block" noWrap color="inherit">
            {getSubcontractorName(shift.assignedSubcontractor)}
          </Typography>
        )}
        {duplicateCount > 1 && (
          <Tooltip title={`${duplicateCount} identical vacant shifts`}>
            <Chip
              sx={{
                mt: 0.5,
                height: 20,
                bgcolor: shiftStyle.fg,
                color: shiftStyle.bg,
                borderColor: shiftStyle.border,
              }}
              label={`x${duplicateCount}`}
            />
            {/* <Box
              sx={{
                px: 0.6,
                borderRadius: 1,
                bgcolor: shiftStyle.fg,
                color: shiftStyle.bg,
                fontSize: '0.65rem',
                fontWeight: 800,
                lineHeight: 1.5,
              }}
            >
              ×{duplicateCount}
            </Box> */}
          </Tooltip>
        )}
      </Stack>

      {!shift.assignedSubcontractor && shift.assignedToSubcontractor && (
        <Typography variant="caption" display="block" noWrap color="inherit">
          Contractor
        </Typography>
      )}
      <Tooltip title={getShiftSource(shift)}>
      <Chip
        size="small"
        label={getShiftSource(shift)}
        sx={{
          mt: 0.5,
          height: 20,
          bgcolor: shiftStyle.chipBg,
          color: shiftStyle.fg,
          borderColor: shiftStyle.border,
        }}
        variant="outlined"
      /></Tooltip>
      {shift?.assignedTo && shift?.acknowledgement?.declinedAt && (
        <Chip
          size="small"
          color="error"
          label="Cannot Attend"
          icon={<HelpOutlineIcon />}
          sx={{ mt: 0.5, height: 20 }}
        />
      )}
    </Box>
  )
}

export default RosterShiftCard
